import { getCoreUtils } from "./kCoreHandler";
import { kHtmlParserCore } from "./kHtmlParserCore";
import { kXmlTextCore } from "./kXmlTextCore";

/**
 * kHTMLCore — server-safe ROOT base for the kHTML widget lineage.
 *
 * The browser kHTML.ts (ReUsableTS) builds LIVE DOM widgets via
 * document.createElement, so it only runs in a page. Everything in a
 * kHTML widget that does NOT need a live DOM — common widget state
 * (Id / Visible), HTML / attribute escaping, and the ToHtmlString()
 * emission contract — is host-neutral and lives here, where the Azure
 * Function HTML→PDF pipeline (and any other project) reuses it without a
 * browser.
 *
 * Lineage: each browser widget `extends` its `*Core` counterpart, and
 * every `*Core` extends THIS class, so kHTMLCore is the single place the
 * core-safe surface of the whole family is defined. This file holds the
 * FOUNDATION members — the generic element builder and the cell→row→table
 * chain. Form-control cores (progress bar, select, radio, checkbox) live
 * in kHTMLWidgetsCore.ts and extend kHTMLCore the same way.
 *
 * Errors route through the central core handler; every method follows
 * guard + try/catch + handler + safe-default.
 */
export class kHTMLCore {
    public Id: string = "";
    public Visible: boolean = true;

    public constructor(Id?: string) {
        if (Id !== undefined) { this.Id = Id; }
    }

    /**
     * Emit this widget as an HTML string. The root returns "" — every
     * concrete `*Core` overrides it. One declared contract lets a
     * container (kHTMLElementCore, kHtmlDocBuilderCore) hold a
     * `kHTMLCore[]` of mixed widgets and serialise them uniformly.
     */
    public ToHtmlString = (): string => {
        return "";
    };

    // HTML text-content escape (&, <, >). Universal to every widget that
    // renders user text, so it lives on the root.
    public static EscapeHtml = (Value: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (typeof Value !== "string") { return ""; }
            let Out: string = Value.split("&").join("&amp;");
            Out = Out.split("<").join("&lt;");
            Out = Out.split(">").join("&gt;");
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHTMLCore.EscapeHtml");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: pure strict text projection; callers own gates, coercion and native errors. */
    public static EscapeHtmlPure = (Value: string): string => {
        return kXmlTextCore.Escape(Value, false);
    };

    // Attribute-value escape (adds quote handling on top of EscapeHtml).
    public static EscapeAttr = (Value: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = kHTMLCore.EscapeHtml(Value);
            Out = Out.split("\"").join("&quot;");
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHTMLCore.EscapeAttr");
            return "";
        }
    };

    /**
     * XML text/attribute escape: `EscapeAttr` plus the APOSTROPHE, which is the one character an XML
     * document can need escaped and an HTML one generally does not. Use this for a value interpolated
     * into XML rather than markup a browser parses — a CSOM/CAML request body, an SVG fragment, an
     * Office Open XML part.
     *
     * The apostrophe is emitted as the NUMERIC reference `&#39;`, not `&apos;`: both are correct XML,
     * but `&apos;` is undefined in HTML4, and these fragments routinely end up somewhere that parses as
     * HTML (an SVG inlined into a page, an XML body echoed into a log viewer). The numeric form cannot
     * be wrong in any of those contexts.
     */
    public static EscapeXml = (Value: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return kHTMLCore.EscapeAttr(Value).split("'").join("&#39;");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHTMLCore.EscapeXml");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: pure strict projection; callers own gates, coercion and native errors. */
    public static EscapeXmlPure = (Value: string): string => {
        return kXmlTextCore.EscapeAttribute(Value, false).replace(/'/g, "&#39;");
    };


    /** DOCUMENTED EXCEPTION: pure strict projection; callers own gates, coercion and native errors. */
    public static EscapeAttrPure = (Value: string): string => {
        return kXmlTextCore.EscapeAttribute(Value, false);
    };}

/**
 * kHTMLElementCore — generic server-safe element builder. The workhorse
 * of the lineage: div / span / p / h1-h6 / section / hr / br / img all
 * collapse to (tag + attributes + class + inline style + children/text).
 * Because Children is `kHTMLCore[]`, ANY widget core (a table, another
 * element, a kSelectCore) nests inside via the shared ToHtmlString()
 * contract — so the HTML→PDF doc builder composes whole pages from these
 * without ever touching a DOM. Core-safe counterpart of kHTML.ts's kDiv
 * and the ad-hoc document.createElement element construction.
 */
export class kHTMLElementCore extends kHTMLCore {
    public Tag: string = "div";
    // Plain text content — HTML-escaped on emit. Used only when InnerHtml
    // is empty and there are no children.
    public Text: string = "";
    // Trusted pre-built inner HTML. When set, emitted verbatim and wins
    // over Text (children still append after it).
    public InnerHtml: string = "";
    public ClassName: string = "";
    public Children: kHTMLCore[] = [];
    private Attributes: { Name: string; Value: string }[] = [];
    private Styles: { Prop: string; Value: string }[] = [];

    public constructor(Tag?: string, Id?: string) {
        super(Id);
        if (Tag !== undefined && Tag !== "") { this.Tag = Tag; }
    }

    public SetText = (Text: string): kHTMLElementCore => {
        if (getCoreUtils().KErrorMode === true) { return this; }
        try {
            this.Text = Text;
            return this;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.SetText");
            return this;
        }
    };

    public SetInnerHtml = (Html: string): kHTMLElementCore => {
        if (getCoreUtils().KErrorMode === true) { return this; }
        try {
            this.InnerHtml = Html;
            return this;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.SetInnerHtml");
            return this;
        }
    };

    public SetClass = (ClassName: string): kHTMLElementCore => {
        if (getCoreUtils().KErrorMode === true) { return this; }
        try {
            this.ClassName = ClassName;
            return this;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.SetClass");
            return this;
        }
    };

    public AddAttribute = (Name: string,
            Value: string): kHTMLElementCore => {
        if (getCoreUtils().KErrorMode === true) { return this; }
        try {
            this.Attributes.push({ Name: Name, Value: Value });
            return this;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.AddAttribute");
            return this;
        }
    };

    public AddStyle = (Prop: string, Value: string): kHTMLElementCore => {
        if (getCoreUtils().KErrorMode === true) { return this; }
        try {
            this.Styles.push({ Prop: Prop, Value: Value });
            return this;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.AddStyle");
            return this;
        }
    };

    // Append any widget core as a child. Returns the CHILD so callers can
    // keep building it (e.g. AddChild(new kHTMLElementCore("p")).SetText).
    public AddChild = (Child: kHTMLCore): kHTMLCore => {
        if (getCoreUtils().KErrorMode === true) { return Child; }
        try {
            this.Children.push(Child);
            return Child;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.AddChild");
            return Child;
        }
    };

    // Convenience: create a child element of the given tag, append it,
    // and return it for chaining.
    public AddElement = (Tag: string): kHTMLElementCore => {
        const Child: kHTMLElementCore = new kHTMLElementCore(Tag);
        if (getCoreUtils().KErrorMode === true) { return Child; }
        try {
            this.Children.push(Child);
            return Child;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.AddElement");
            return Child;
        }
    };

    public ToHtmlString = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Visible === false) { return ""; }
            const TagName: string = this.Tag.toLowerCase();
            const Open: string = "<" + TagName + this.BuildAttrs() + ">";
            if (kHtmlParserCore.IsVoidTag(TagName)) {
                return Open;
            }
            return Open + this.BuildInner() + "</" + TagName + ">";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.ToHtmlString");
            return "";
        }
    };

    private BuildInner = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Inner: string = "";
            if (this.InnerHtml !== "") {
                Inner = this.InnerHtml;
            } else if (this.Children.length === 0) {
                Inner = kHTMLCore.EscapeHtml(this.Text);
            }
            const ChildParts: string[] = [];
            for (const Child of this.Children) {
                ChildParts.push(Child.ToHtmlString());
            }
            return Inner + ChildParts.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.BuildInner");
            return "";
        }
    };

    private BuildAttrs = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = "";
            if (this.Id !== "") {
                Out = Out + " id=\"" + kHTMLCore.EscapeAttr(this.Id) + "\"";
            }
            if (this.ClassName !== "") {
                Out = Out + " class=\""
                    + kHTMLCore.EscapeAttr(this.ClassName) + "\"";
            }
            for (const A of this.Attributes) {
                Out = Out + " " + A.Name + "=\""
                    + kHTMLCore.EscapeAttr(A.Value) + "\"";
            }
            const Style: string = this.BuildStyle();
            if (Style !== "") {
                Out = Out + " style=\"" + Style + "\"";
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.BuildAttrs");
            return "";
        }
    };

    private BuildStyle = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Styles.length === 0) { return ""; }
            const Parts: string[] = [];
            for (const S of this.Styles) {
                Parts.push(S.Prop + ": " + S.Value);
            }
            return Parts.join("; ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLElementCore.BuildStyle");
            return "";
        }
    };
}

/**
 * kHTMLCellCore — server-safe table cell model. Core-only (the browser
 * uses raw HTMLTableCellElement), so no browser subclass; the doc builder
 * and kHTMLRowCore compose these directly.
 */
export class kHTMLCellCore extends kHTMLCore {
    // Plain text — HTML-escaped on emit. Ignored when Html is non-empty.
    public Text: string = "";
    // Pre-built trusted inner HTML. When set, emitted verbatim (caller
    // owns its safety — used for nested builder output, not user input).
    public Html: string = "";
    public ClassName: string = "";
    public ColSpan: number = 1;
    // CSS text-align value ("left" | "right" | "center" | "").
    public Align: string = "";
    public MinWidthPx: number = 0;
    public IsHeader: boolean = false;

    public ToHtmlString = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Tag: string = "td";
            if (this.IsHeader === true) { Tag = "th"; }
            let Attrs: string = "";
            if (this.ClassName !== "") {
                Attrs = Attrs + " class=\""
                    + kHTMLCore.EscapeAttr(this.ClassName) + "\"";
            }
            if (this.ColSpan > 1) {
                Attrs = Attrs + " colspan=\"" + String(this.ColSpan) + "\"";
            }
            const Style: string = this.BuildStyle();
            if (Style !== "") {
                Attrs = Attrs + " style=\"" + Style + "\"";
            }
            let Inner: string = kHTMLCore.EscapeHtml(this.Text);
            if (this.Html !== "") { Inner = this.Html; }
            return "<" + Tag + Attrs + ">" + Inner + "</" + Tag + ">";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLCellCore.ToHtmlString");
            return "";
        }
    };

    private BuildStyle = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Parts: string[] = [];
            if (this.Align !== "") {
                Parts.push("text-align: " + this.Align);
            }
            if (this.MinWidthPx > 0) {
                Parts.push("min-width: " + String(this.MinWidthPx) + "px");
            }
            if (Parts.length === 0) { return ""; }
            return Parts.join("; ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLCellCore.BuildStyle");
            return "";
        }
    };
}

/**
 * kHTMLRowCore — server-safe half of kHTML.ts's kHTMLRow. Holds the cell
 * model (CellModels — deliberately NOT named `Cells`, which the browser
 * kHTMLRow uses for its live HTMLTableCellElement[]) and string emission.
 * The browser kHTMLRow extends this and adds DOM cell construction; the
 * core cell-adders carry the *Core suffix so they never collide with the
 * browser's DOM AddCell / AddNumberCell / AddMergedCell.
 */
export class kHTMLRowCore extends kHTMLCore {
    public CellModels: kHTMLCellCore[] = [];
    public ClassName: string = "";

    // Append a plain-text cell; returns it so the caller can tweak
    // alignment / colspan before emit.
    public AddCellCore = (Text: string,
            ClassName?: string): kHTMLCellCore => {
        const Cell: kHTMLCellCore = new kHTMLCellCore();
        if (getCoreUtils().KErrorMode === true) { return Cell; }
        try {
            Cell.Text = Text;
            if (ClassName !== undefined) { Cell.ClassName = ClassName; }
            this.CellModels.push(Cell);
            return Cell;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLRowCore.AddCellCore");
            return Cell;
        }
    };

    // Append a cell whose inner content is trusted pre-built HTML.
    public AddHtmlCellCore = (Html: string,
            ClassName?: string): kHTMLCellCore => {
        const Cell: kHTMLCellCore = new kHTMLCellCore();
        if (getCoreUtils().KErrorMode === true) { return Cell; }
        try {
            Cell.Html = Html;
            if (ClassName !== undefined) { Cell.ClassName = ClassName; }
            this.CellModels.push(Cell);
            return Cell;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLRowCore.AddHtmlCellCore");
            return Cell;
        }
    };

    // Right-aligned numeric cell, mirroring kHTMLRow.AddNumberCell.
    public AddNumberCellCore = (Value: number): kHTMLCellCore => {
        const Cell: kHTMLCellCore = new kHTMLCellCore();
        if (getCoreUtils().KErrorMode === true) { return Cell; }
        try {
            Cell.Text = String(Value);
            Cell.Align = "right";
            this.CellModels.push(Cell);
            return Cell;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLRowCore.AddNumberCellCore");
            return Cell;
        }
    };

    public AddMergedCellCore = (Text: string,
            Cols: number): kHTMLCellCore => {
        const Cell: kHTMLCellCore = new kHTMLCellCore();
        if (getCoreUtils().KErrorMode === true) { return Cell; }
        try {
            Cell.Text = Text;
            Cell.ColSpan = Cols;
            this.CellModels.push(Cell);
            return Cell;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLRowCore.AddMergedCellCore");
            return Cell;
        }
    };

    public ToHtmlString = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Parts: string[] = [];
            for (const Cell of this.CellModels) {
                Parts.push(Cell.ToHtmlString());
            }
            let Attrs: string = "";
            if (this.Id !== "") {
                Attrs = Attrs + " id=\""
                    + kHTMLCore.EscapeAttr(this.Id) + "\"";
            }
            if (this.ClassName !== "") {
                Attrs = Attrs + " class=\""
                    + kHTMLCore.EscapeAttr(this.ClassName) + "\"";
            }
            return "<tr" + Attrs + ">" + Parts.join("") + "</tr>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLRowCore.ToHtmlString");
            return "";
        }
    };
}

/**
 * kHTMLTableCore — host-neutral half of kHTMLTable. Owns the CSS, the
 * header/body/footer row model (kHTMLRowCore), HTML-string emission. The
 * browser kHTMLTable extends this; the HTML→PDF builder uses it directly.
 * The core builders carry the *Core suffix so they never collide with the
 * browser's DOM AddRow / AddHeaders / AddBorders.
 */
export class kHTMLTableCore extends kHTMLCore {
    public Borders: boolean = false;
    public Rounded: boolean = false;
    public HeaderRows: kHTMLRowCore[] = [];
    public BodyRows: kHTMLRowCore[] = [];
    public FooterRows: kHTMLRowCore[] = [];

    public AddBordersCore = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Borders = true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLTableCore.AddBordersCore");
        }
    };

    // Build a header row of <th> cells. Mirrors kHTMLTable.AddHeaders'
    // (titles, minWidths?, alignments?) signature so the model fills the
    // same way the DOM path does.
    public AddHeadersCore = (Titles: string[],
            MinWidths?: number[], Alignments?: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Row: kHTMLRowCore = new kHTMLRowCore();
            let I: number = 0;
            for (const Title of Titles) {
                const Cell: kHTMLCellCore = new kHTMLCellCore();
                Cell.IsHeader = true;
                Cell.Text = Title;
                Cell.Align = "left";
                if (Alignments !== undefined && Alignments.length > I) {
                    Cell.Align = Alignments[I];
                }
                if (MinWidths !== undefined && MinWidths.length > I) {
                    Cell.MinWidthPx = MinWidths[I];
                }
                Row.CellModels.push(Cell);
                I = I + 1;
            }
            this.HeaderRows.push(Row);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLTableCore.AddHeadersCore");
        }
    };

    // Append an empty body row and return it for cell population.
    public AddRowCore = (): kHTMLRowCore => {
        const Row: kHTMLRowCore = new kHTMLRowCore();
        if (getCoreUtils().KErrorMode === true) { return Row; }
        try {
            this.BodyRows.push(Row);
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLTableCore.AddRowCore");
            return Row;
        }
    };

    public AddFooterRowCore = (): kHTMLRowCore => {
        const Row: kHTMLRowCore = new kHTMLRowCore();
        if (getCoreUtils().KErrorMode === true) { return Row; }
        try {
            this.FooterRows.push(Row);
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLTableCore.AddFooterRowCore");
            return Row;
        }
    };

    // Emit the complete <table> as a string. Applies the kBlackTable
    // class when Borders is set; an inline border-radius when Rounded.
    public ToHtmlString = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Visible === false) { return ""; }
            let Attrs: string = "";
            if (this.Id !== "") {
                Attrs = Attrs + " id=\""
                    + kHTMLCore.EscapeAttr(this.Id) + "\"";
            }
            if (this.Borders === true) {
                Attrs = Attrs + " class=\"kBlackTable\"";
            }
            if (this.Rounded === true) {
                Attrs = Attrs + " style=\"border:thin solid grey;"
                    + "border-radius:5px;margin:5px\"";
            }
            const Head: string = this.SectionHtml("thead", this.HeaderRows);
            const Body: string = this.SectionHtml("tbody", this.BodyRows);
            const Foot: string = this.SectionHtml("tfoot", this.FooterRows);
            return "<table" + Attrs + ">" + Head + Body + Foot + "</table>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLTableCore.ToHtmlString");
            return "";
        }
    };

    private SectionHtml = (Tag: string,
            Rows: kHTMLRowCore[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Rows.length === 0) { return ""; }
            const Parts: string[] = [];
            for (const Row of Rows) { Parts.push(Row.ToHtmlString()); }
            return "<" + Tag + ">" + Parts.join("") + "</" + Tag + ">";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kHTMLTableCore.SectionHtml");
            return "";
        }
    };

    // The bordered/striped table CSS, hoisted verbatim from kHTMLTable so
    // browser AddStyles() and the HTML→PDF stylesheet share one source.
    public static GetStyle = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "\n"
                + ".kBlackTable{\n"
                + "    width: 100%;\n"
                + "    text-align: left;\n"
                + "    border-collapse: collapse;\n"
                + "}\n"
                + ".kBlackTable td, .kBlackTable th {\n"
                + "    border: 1px solid #000000;\n"
                + "    padding: 5px 4px;\n"
                + "}\n"
                + ".kBlackTable thead:first-child {\n"
                + "    background:#038387;\n"
                + "    border-bottom: 3px solid #000000;\n"
                + "    color: #FFFFFF;\n"
                + "}\n"
                + ".kBlackTable thead th {\n"
                + "    font-weight: bold;\n"
                + "    color: #FFFFFF;\n"
                + "    background: #038387;\n"
                + "    text-align: left;\n"
                + "}\n"
                + ".kBlackTable tr:nth-child(even) {\n"
                + "    background: lightgrey;\n"
                + "}\n";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHTMLTableCore.GetStyle");
            return "";
        }
    };
}
